import { createReducer } from "@reduxjs/toolkit";
import moment from "moment";
import { InviteEntryRightResponse } from "../../../api/entryRights.api";
import { GetVehicleAssignmentsMyFleetQuery, GetVehicleAssignmentsQuery } from "../../../api/parkingProducts.api";
import {
  AssignedParkingRight,
  DelegatedParkingRight,
  DelegeeWithProductsModel,
  VehicleAssignment,
  VehicleAssignmentModel,
  VehicleAssignmentMyFleet
} from "../../../models/delegations/DelegatedParkingProductsModel";
import { ErrorDetailsModel } from "../../../models/ErrorDetailsModel";
import { InsufficientAvailabilityDetail } from "../../../models/vehicle-assignments/ValidateVehicleBulkUpload";
import { LocatedParkingProduct } from "../../../models/LocatedParkingProduct";
import { ParkingProductModel, ParkingProductType } from "../../../models/ParkingProductModel";
import { parkingProductsTypes, strictParkingProductsTypes } from "../../action-types/parkingProducts.types";
import * as entryRightsActions from "../../actions/entryRights.actions";
import { ParkingProductsAction, ParkingProductsActionType } from "../../actions/parkingProducts.actions";
import * as parkingProductsThunks from "../../actions/parkingProducts.thunks";
import { failure, request, success } from "../../helpers/action.helper";
import { incrementTotalAssignedCount, reduceDeleteLocatedParkingProducts, reduceRevokeMyFleet, reduceRevokeMyFleetOverview, reduceVehicleAssignmentMutation } from "../../helpers/dva-optimistic-updates";
import { applyOptimisticDeleteMyFleet, applyOptimisticDeleteMyProducts, applyOptimisticInviteParkingRightMyProducts, applyOptimisticRevokeMyFleet, applyOptimisticRevokeMyProducts, applyOptimisticUpdateParkingRightMyFleet, applyOptimisticUpdateParkingRightMyProducts } from "../../helpers/parkingright-optimistic-updates";
import reduceBulkVehicleAssignments from "./bulk-dva";

type Pending = {
  loading: boolean;
};

export type DataType<T> = {
  loading: boolean;
  data?: T | null;
  error?: ErrorDetailsModel | null;
};

type PagedDataType<T> = {
  [page: number]: DataType<T>;
};

export type VehiclesMyProductsState = {
  totalPages: number;
  totalRecords: number;
  pages: {
    [page: number]: {
      loading: boolean;
      data?: AssignedParkingRight[] | null;
      error?: ErrorDetailsModel | null;
      query: GetVehicleAssignmentsQuery;
      requestId?: string;
    };
  };
};

export type VehicleAssignmentsMyFleetState = {
  totalPages: number;
  totalRecords: number;
  pages: {
    [page: number]: {
      loading: boolean;
      data?: VehicleAssignment[] | null;
      error?: ErrorDetailsModel | null;
      query: GetVehicleAssignmentsQuery;
    };
  };
};

export type VehicleAssignmentsMyFleetOverviewState = {
  totalPages: number;
  totalRecords: number;
  totalUniqueVehicles: number;
  pages: {
    [page: number]: {
      loading: boolean;
      data?: VehicleAssignmentMyFleet[] | null;
      error?: ErrorDetailsModel | null;
      query: GetVehicleAssignmentsMyFleetQuery;
    };
  };
};

export type Delegee = {
  totalPages: number;
  totalRecords: number;
  pages: PagedDataType<DelegeeWithProductsModel[]> | null;
}

export type DelegatedParkingProducts = {
  totalPages: number;
  totalRecords: number;
  pages: PagedDataType<DelegatedParkingRight[]> | null;
}

export type ParkingProductState = {
  loading: boolean;
  parkingProducts: DataType<ParkingProductModel[]>;
  locatedParkingProducts: DataType<LocatedParkingProduct[]>;
  delegatedParkingProducts: DelegatedParkingProducts;
  delegees: Delegee;
  vehiclesMyProducts: VehiclesMyProductsState;
  vehicleAssignmentsMyFleet: VehicleAssignmentsMyFleetState;
  vehicleAssignmentsMyFleetOverview: VehicleAssignmentsMyFleetOverviewState;
  delegeeDetails: DataType<DelegeeWithProductsModel>;
  importDelegees: Pending;
  validateDelegees: Pending & {
    success?: boolean;
    fileReference?: string;
  };
  selectedVehicleAssignment: DataType<VehicleAssignmentModel>
  bulkImportProgress: {
    progress: number;
    hasErrors: boolean;
    numberOfErrors: number;
  };
  isBulkImportCompleted: boolean;
  error: ErrorDetailsModel | null;
  hasUnlimitedEntryRights: boolean;
  calculatedWhitelistDelta: Pending & {
    amountOfVehiclesToUpdate: number;
    amountOfVehiclesToCreate: number;
    amountOfVehiclesToDelete: number;
    amountOfVehiclesWithNoChanges: number;
    hasChanges: boolean;
    fileReference: string;
  }
  bulkVehicleAssignment: Pending;
  bulkVehicleImportProgress: {
    progress: number;
    errorCount: number;
    warningCount: number;
  };
  isBulkVehicleAssignmentsCompleted: boolean;
  validatedVehicleBulkUpload: Pending & {
    amountOfVehiclesToCreate: number;
    amountOfExceedingVehicles: number;
    fileReference: string;
    insufficientAvailabilityDetails?: InsufficientAvailabilityDetail[];
  };
  selectedParkingRightProductPlaceId: string | undefined;
};

const initialState: ParkingProductState = {
  loading: false,
  parkingProducts: {
    loading: false,
    data: [],
    error: null,
  },
  locatedParkingProducts: {
    loading: false,
    data: [],
    error: null,
  },
  delegatedParkingProducts: {
    totalPages: 0,
    totalRecords: 0,
    pages: null,
  },
  delegees: {
    totalPages: 0,
    totalRecords: 0,
    pages: null,
  },
  delegeeDetails: {
    loading: false,
    data: null,
    error: null,
  },
  importDelegees: {
    loading: false,
  },
  validateDelegees: {
    loading: false,
  },
  error: null,
  bulkImportProgress: {
    progress: 0,
    hasErrors: false,
    numberOfErrors: 0,
  },
  isBulkImportCompleted: true,
  vehiclesMyProducts: {
    pages: {},
    totalPages: 0,
    totalRecords: 0,
  },
  vehicleAssignmentsMyFleet: {
    pages: {},
    totalPages: 0,
    totalRecords: 0,
  },
  vehicleAssignmentsMyFleetOverview: {
    pages: {},
    totalPages: 0,
    totalRecords: 0,
    totalUniqueVehicles: 0,
  },
  selectedVehicleAssignment: {
    loading: false,
  },
  hasUnlimitedEntryRights: false,
  calculatedWhitelistDelta: {
    amountOfVehiclesToCreate: 0,
    amountOfVehiclesToDelete: 0,
    amountOfVehiclesToUpdate: 0,
    amountOfVehiclesWithNoChanges: 0,
    fileReference: "",
    hasChanges: false,
    loading: false
  },
  bulkVehicleAssignment: {
    loading: false,
  },
  bulkVehicleImportProgress: {
    progress: 0,
    errorCount: 0,
    warningCount: 0,
  },
  isBulkVehicleAssignmentsCompleted: true,
  validatedVehicleBulkUpload: {
    loading: false,
    amountOfVehiclesToCreate: 0,
    amountOfExceedingVehicles: 0,
    fileReference: "",
    insufficientAvailabilityDetails: [],
  },
  selectedParkingRightProductPlaceId: undefined,
};

function reduceVehicleAssignmentUpdates(
  state: ParkingProductState,
  action: ParkingProductsAction
): ParkingProductState {
  switch (action.type) {
    case strictParkingProductsTypes.SET_MY_FLEET_VEHICLES_TOTAL_RECORDS:
      return {
        ...state,
        vehiclesMyProducts: {
          ...state.vehiclesMyProducts,
          totalRecords: action.payload.totalRecords,
        },
      }
    case request(parkingProductsTypes.ASSIGN_VEHICLE):
      return { ...state, loading: true };

    case request(parkingProductsTypes.UPDATE_VEHICLE_ASSIGNMENT):
      return {
        ...state,
        loading: true,
      };
    case success(parkingProductsTypes.ASSIGN_VEHICLE):
    case success(parkingProductsTypes.UPDATE_VEHICLE_ASSIGNMENT): {
      const newState = reduceVehicleAssignmentMutation(state, action.assignedVehicleMutation!);
      return { ...state, ...newState, loading: false };
    }

    case failure(parkingProductsTypes.UPDATE_VEHICLE_ASSIGNMENT):
    case failure(parkingProductsTypes.ASSIGN_VEHICLE):
      return {
        ...state,
        loading: false,
        error: action.error as ErrorDetailsModel,
      };

    default:
      return state;
  }
}

function reduceVehicleAssignmentsMyFleet(vehicleAssignments: VehicleAssignmentsMyFleetState, action: ParkingProductsActionType): VehicleAssignmentsMyFleetState {
  let pageNumber: number;
  switch (action.type) {
    case request(parkingProductsTypes.GET_VEHICLE_ASSIGNMENTS_MY_FLEET):
      pageNumber = action.vehicleAssigmentsMyFleetPayload?.pageNumber!;

      return {
        ...vehicleAssignments,
        pages: {
          ...vehicleAssignments.pages,
          [pageNumber]: {
            ...vehicleAssignments.pages[pageNumber],
            loading: true,
            data: [],
            query: action.vehicleAssigmentsMyFleetPayload?.query!,
          },
        },
      };

    case success(parkingProductsTypes.GET_VEHICLE_ASSIGNMENTS_MY_FLEET):
      pageNumber = action.vehicleAssigmentsMyFleetPayload?.pageNumber!;
      return {
        ...vehicleAssignments,
        totalPages: action.vehicleAssigmentsMyFleetPayload?.totalPages as number,
        totalRecords: action.vehicleAssigmentsMyFleetPayload?.totalRecords as number,
        pages: {
          ...vehicleAssignments.pages,
          ...{
            [pageNumber]: {
              ...vehicleAssignments.pages[pageNumber],
              loading: false,
              data: action.vehicleAssigmentsMyFleetPayload?.payload,
            },
          },
        },
      };

    case failure(parkingProductsTypes.GET_VEHICLE_ASSIGNMENTS_MY_FLEET):
      pageNumber = action.vehicleAssigmentsMyFleetPayload?.pageNumber as number;

      return {
        ...vehicleAssignments,
        pages: {
          ...vehicleAssignments.pages,
          ...{
            [pageNumber]: {
              ...vehicleAssignments.pages[pageNumber],
              data: [],
              loading: false,
              error: action.error,
            },
          },
        },
      };

    default:
      return vehicleAssignments;
  }
}

function reduceGlobalLoadingState(state: ParkingProductState, action: ParkingProductsActionType) : ParkingProductState {
  switch (action.type) {
    case request(parkingProductsTypes.RESEND_DELEGEE_INVITE):
    case request(parkingProductsTypes.REVOKE_PARKINGRIGHTS):
    case request(parkingProductsTypes.REVOKE_VEHICLE_ASSIGNMENTS):
    case request(parkingProductsTypes.DELETE_VEHICLE_ASSIGNMENTS):
    case request(parkingProductsTypes.DELETE_DELEGEES):
    case request(parkingProductsTypes.GET_BULK_IMPORT_STATUS):
    case request(parkingProductsTypes.GET_ASSIGNED_VEHICLE):
        return {
        ...state,
        selectedVehicleAssignment: {
          loading: true,
        },
        loading: true,
      };
    case failure(parkingProductsTypes.REVOKE_PARKINGRIGHTS):
    case failure(parkingProductsTypes.REVOKE_VEHICLE_ASSIGNMENTS):
    case failure(parkingProductsTypes.DELETE_VEHICLE_ASSIGNMENTS):
    case failure(parkingProductsTypes.DELETE_DELEGEES):
    case failure(parkingProductsTypes.GET_ASSIGNED_VEHICLE):
        return {
        ...state,
        selectedVehicleAssignment: {
          loading: false,
          error: action.error as ErrorDetailsModel,
        },
        loading: false,
        error: action.error as ErrorDetailsModel,
      };
    case success(parkingProductsTypes.DELETE_VEHICLE_ASSIGNMENTS):
      return reduceDeleteLocatedParkingProducts(
        { ...state, loading: false },
        action.deletedVehicleAssignmentIds ?? []
      );
    case success(parkingProductsTypes.RESEND_DELEGEE_INVITE):
    case success(parkingProductsTypes.ASSIGN_VEHICLE):
      return {
        ...state,
        loading: false,
      };
    case success(parkingProductsTypes.REVOKE_VEHICLE_ASSIGNMENTS): {
      const pmcToDecrement: Record<number, number> = {};
      for (const vap of action.revokeProductsModel!.vehicleAssignmentProducts) {
        pmcToDecrement[vap.pmcId] = (pmcToDecrement[vap.pmcId] ?? 0) - 1;
      }
      return {
        ...state,
        locatedParkingProducts: state.locatedParkingProducts.data ? {
          ...state.locatedParkingProducts,
          data: incrementTotalAssignedCount(state.locatedParkingProducts.data, pmcToDecrement),
        } : state.locatedParkingProducts,
        vehicleAssignmentsMyFleet: reduceRevokeMyFleet(state.vehicleAssignmentsMyFleet, action.revokeProductsModel!),
        vehicleAssignmentsMyFleetOverview: reduceRevokeMyFleetOverview(state.vehicleAssignmentsMyFleetOverview, action.revokeProductsModel!),
        loading: false,
      };
    }
    case success(parkingProductsTypes.DELETE_DELEGEE):
    case success(parkingProductsTypes.DELETE_DELEGEES):
      applyOptimisticDeleteMyFleet(state, action);
      applyOptimisticDeleteMyProducts(state, action);
      state.loading = false;
      return state;
    case success(parkingProductsTypes.REVOKE_PARKINGRIGHTS):
      applyOptimisticRevokeMyFleet(state, action.revokedParkingRightIds ?? []);
      applyOptimisticRevokeMyProducts(state, action.revokedParkingRightIds ?? []);
      state.loading = false;
      return state;
    default:
      return state;
  }
}

function oldReducer(
  state: ParkingProductState = initialState,
  action: ParkingProductsActionType
): ParkingProductState {
  let pageNumber: number;

  const newStateMaybe = reduceVehicleAssignmentUpdates(state, action);
  if (newStateMaybe != state) {
    return newStateMaybe;
  }

  const newVehicleAssignmentsState = reduceVehicleAssignmentsMyFleet(state.vehicleAssignmentsMyFleet, action);
  if (newVehicleAssignmentsState !== state.vehicleAssignmentsMyFleet) {
    return {
      ...state,
      vehicleAssignmentsMyFleet: newVehicleAssignmentsState,
    };
  }

  const newLoadingState = reduceGlobalLoadingState(state, action);
  if (newLoadingState !== state) {
    return newLoadingState;
  }

  switch (action.type) {
    case request(parkingProductsTypes.GET_DELEGATED_PARKING_RIGHTS):
      pageNumber = action.delegatedParkingProductsPayload?.pageNumber as number;

      return {
        ...state,
        selectedParkingRightProductPlaceId: action.selectedParkingRightProductPlaceId,
        delegatedParkingProducts: {
          ...state.delegatedParkingProducts,
          pages: {
            ...state.delegatedParkingProducts.pages,
            [pageNumber]: {
              loading: true,
              data: [],
            },
          },
        },
      };
    case request(parkingProductsTypes.IMPORT_DELEGEES):
      return {
        ...state,
        importDelegees: {
          ...state.importDelegees,
          loading: true,
        },
        bulkImportProgress: {
          progress: 0,
          hasErrors: false,
          numberOfErrors: 0,
        },
      };
    case request(parkingProductsTypes.VALIDATE_DELEGEES):
      return {
        ...state,
        validateDelegees: {
          ...state.validateDelegees,
          loading: true,
        },
      };
    case success(parkingProductsTypes.GET_DELEGATED_PARKING_RIGHTS):
      pageNumber = action.delegatedParkingProductsPayload?.pageNumber as number;

      return {
        ...state,
        delegatedParkingProducts: {
          ...state.delegatedParkingProducts,
          totalPages: action.delegatedParkingProductsPayload?.totalPages as number,
          totalRecords: action.delegatedParkingProductsPayload?.totalRecords as number,
          pages: {
            ...state.delegatedParkingProducts.pages,
            ...{
              [pageNumber]: {
                loading: false,
                data: action.delegatedParkingProductsPayload?.payload,
              },
            },
          },
        },
      };
    case success(parkingProductsTypes.GET_ASSIGNED_VEHICLE):
      return {
        ...state,
        selectedVehicleAssignment: {
          loading: false,
          data: action.selectedVehicleAssignment,
        },
        loading: false,
      };
    case success(parkingProductsTypes.GET_BULK_IMPORT_PROGRESS):
      return {
        ...state,
        bulkImportProgress: {
          progress: action.bulkImportProgress?.progress ?? 0,
          hasErrors: action.bulkImportProgress?.hasErrors ?? false,
          numberOfErrors: action.bulkImportProgress?.numberOfErrors ?? 0,
        },
      };
    case success(parkingProductsTypes.GET_BULK_IMPORT_STATUS):
    case failure(parkingProductsTypes.GET_BULK_IMPORT_STATUS):
      return {
        ...state,
        isBulkImportCompleted: action.isBulkImportCompleted ?? true,
        loading: false,
      };
    case success(parkingProductsTypes.IMPORT_DELEGEES):
      return {
        ...state,
        importDelegees: {
          ...state.importDelegees,
          loading: false,
        },
      };
    case success(parkingProductsTypes.VALIDATE_DELEGEES):
      return {
        ...state,
        validateDelegees: {
          ...state.validateDelegees,
          loading: false,
          success: true,
          fileReference: action.validateDelegeesPayload!.fileReference,
        },
      };
    case failure(parkingProductsTypes.VALIDATE_DELEGEES):
      return {
        ...state,
        validateDelegees: {
          ...state.validateDelegees,
          loading: false,
          success: false,
        },
      };
    case failure(parkingProductsTypes.GET_DELEGATED_PARKING_RIGHTS):
      pageNumber = action.delegatedParkingProductsPayload?.pageNumber as number;

      return {
        ...state,
        delegatedParkingProducts: {
          ...state.delegatedParkingProducts,
          pages: {
            ...state.delegatedParkingProducts.pages!,
            ...{
              [pageNumber]: {
                data: [],
                loading: false,
                error: action.error,
              },
            },
          },
        },
      };
    case failure(parkingProductsTypes.RESEND_DELEGEE_INVITE):
      return {
        ...state,
        loading: false,
        error: action.error as ErrorDetailsModel,
      };
    case failure(parkingProductsTypes.IMPORT_DELEGEES):
      return {
        ...state,
        importDelegees: {
          ...state.importDelegees,
          loading: false,
        },
      };
    case parkingProductsTypes.DISPOSE_ERROR:
      return {
        ...state,
        loading: false,
        error: null,
      };
    case parkingProductsTypes.CLEAR_DELEGEE_DETAILS:
      return {
        ...state,
        delegeeDetails: {
          ...initialState.delegeeDetails,
        },
      };
    default:
      return reduceBulkVehicleAssignments(state, action, () => state);
  }
};


function updateLocatedProductCounters(draft: DataType<LocatedParkingProduct[]>, opts: {pmcId: number; delta: number;}[]) {
    if (!draft.data) {
        return;
    }
    updateParkingRightCounters(draft.data, opts);
    updatePedestrianCounters(draft.data, opts);    
}

function getInviteProductDelta(request: { products: { pmcId: number; activationDate?: string | null }[] }, pmcId: number) {
  const product = request.products.find(p => p.pmcId === pmcId);
  return product?.activationDate && new Date(product.activationDate) > new Date() ? 0 : 1;
}

function getUpdateProductDelta(
  request: { model: { pmcs: { pmcId: number; activationDate?: string | null }[] } },
  pmcId: number,
  oldPmcIds: number[],
  newPmcIds: number[]
) {
  const delta = calculateDelta(pmcId, oldPmcIds, newPmcIds);
  if (delta !== 1) {
    return delta;
  }

  const product = request.model.pmcs.find(p => p.pmcId === pmcId);
  return product?.activationDate && new Date(product.activationDate) > new Date() ? 0 : delta;
}

const updatePedestrianCounters = (locatedParkingProducts: LocatedParkingProduct[], opts: { pmcId: number; delta: number; }[]) => {
  for (const loc of locatedParkingProducts) {
    let locationPedestrianDelta = 0;
    for (const product of loc.parkingProducts) {
      const pmcDelta = opts.find(o => o.pmcId === product.pmcId)?.delta ?? 0;
      if (product.productType !== ParkingProductType.PedestrianAccess) { 
        continue; 
      }
      const newTotalDelegatedPedestrianRights = Math.max(0, product.totalDelegatedPedestrianRights + pmcDelta);
      const actualPmcDeltaApplied = newTotalDelegatedPedestrianRights - product.totalDelegatedPedestrianRights;
      product.totalDelegatedPedestrianRights = newTotalDelegatedPedestrianRights;
      locationPedestrianDelta += actualPmcDeltaApplied;
    }
    loc.totalDelegatedPedestrianRights += locationPedestrianDelta;
  }
}

const updateParkingRightCounters = (locatedParkingProducts: LocatedParkingProduct[], opts: { pmcId: number; delta: number; }[]) => {
  for (const loc of locatedParkingProducts) {
    let locationParkingRightDelta = 0;
    for (const product of loc.parkingProducts) {
      const pmcDelta = opts.find(o => o.pmcId === product.pmcId)?.delta ?? 0;
      if (product.productType !== ParkingProductType.ParkingRights) { 
        continue; 
      }
      const newTotalDelegatedParkingRights = Math.max(0, product.totalDelegatedParkingRights! + pmcDelta);
      const actualPmcDeltaApplied = newTotalDelegatedParkingRights - product.totalDelegatedParkingRights!;
      product.totalDelegatedParkingRights = newTotalDelegatedParkingRights;
      locationParkingRightDelta += actualPmcDeltaApplied;
    }
    loc.totalDelegatedParkingRights += locationParkingRightDelta;
  }
}

function applyOptimisticInviteOnParkingMyFleet(state: ParkingProductState, response: InviteEntryRightResponse, request: { name?: string; email: string; }) {
    state.delegees.totalRecords += 1;
    for (const page in state.delegees.pages) {
        const currentPage = state.delegees.pages[page as any as number];
        const newDelegee: DelegeeWithProductsModel = {
            name: request.name!,
            registrationId: response.delegeeId,
            emailAddress: request.email,
            invitationDate: moment().toISOString(),
            invitationAccepted: false,
            products: response.assignedProducts.map(p => {
                const product = state.parkingProducts.data?.find(x => x.pmcId == p.pmcId);

                return ({
                    id: 0, // backend never sets this one??
                    status: 0,
                    contractType: p.contractType,
                    productType: p.productType,
                    created: moment().toDate(),
                    pmcId: p.pmcId,
                    parkingRightId: p.entryRightId,
                    location: product?.location!,
                    name: product?.pmcName!
                })
            })
        };
        currentPage.data!.unshift(newDelegee);
    }
}

export default createReducer(initialState, (builder) => {
    builder
    .addCase(entryRightsActions.inviteActionThunk.fulfilled, (state, action) => {
      state.loading = false;
        if (!action.payload.ok) { 
          state.error = action.payload.error;
          return;
        }

       updateLocatedProductCounters(state.locatedParkingProducts, action.payload.value.assignedProducts.map(p => ({
         pmcId: p.pmcId,
         delta: getInviteProductDelta(action.meta.arg, p.pmcId),
       })));
       applyOptimisticInviteOnParkingMyFleet(state, action.payload.value, action.meta.arg);
    })
    .addCase(entryRightsActions.inviteActionThunk.pending, (state, action) => {
        state.loading = true;
    })
    .addCase(entryRightsActions.inviteActionThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || null;
    })
    .addCase(entryRightsActions.revokeEntryRightsSuccessAction, (state, action) => {
        const dict : { [pmcId: number]: number } = {};
      for (const delegee of action.payload.model.delegeeRevokeModels) {
            for (const product of delegee.products) {
                if (!dict[product.pmcId]) {
                    dict[product.pmcId] = 0;
                }
                dict[product.pmcId] -= 1;
            }
        }
        updateLocatedProductCounters(
            state.locatedParkingProducts,
            Object.entries(dict).map(([pmcId, delta]) => ({ pmcId: parseInt(pmcId), delta })));
    })
    .addCase(entryRightsActions.deleteEntryDelegeesSuccessAction, (state, action) => {
      const pmcIds: number[] = state.locatedParkingProducts.data!.flatMap(lpp => lpp.parkingProducts.map(p => p.pmcId!));
      updateLocatedProductCounters(state.locatedParkingProducts, pmcIds.map(pmcId => ({pmcId: pmcId, delta: action.payload.delegeePmcIds.includes(pmcId) ? -1 : 0})));
    })
    .addCase(entryRightsActions.updateEntryRightDelegeeActionThunk.fulfilled, (state, action) => {
        if (!action.payload.ok) {
            return;
        }
        const { response, original } = action.payload.value;
        const oldPmcIds = action.meta.arg.delegeePmcIds;
        const newPmcIds = response.assignedPedestrianProducts.map(p => p.pmcId);
        const pmcIds: number[] = state.locatedParkingProducts.data!.flatMap(lpp => lpp.parkingProducts.map(p => p.pmcId!));
        updateLocatedProductCounters(state.locatedParkingProducts, pmcIds.map(pmcId => ({
          pmcId,
          delta: getUpdateProductDelta(action.meta.arg, pmcId, oldPmcIds, newPmcIds),
        })));

        if (original) {
            const firstPaProductAdded = response.assignedPedestrianProducts.some(p => p.productType === ParkingProductType.ParkingRights) &&
                !original.products.some(p => p.productType === ParkingProductType.ParkingRights)
            if (firstPaProductAdded) {
                state.delegees.totalRecords += 1; 
            }

            const lastPaProductRemoved = !response.assignedPedestrianProducts.some(p => p.productType === ParkingProductType.ParkingRights) &&
                original.products.some(p => p.productType === ParkingProductType.ParkingRights)
            if (lastPaProductRemoved) {
                state.delegees.totalRecords -= 1; 
            }

            // don't allow negative totals
            state.delegees.totalRecords = Math.max(0, state.delegees.totalRecords);
        }
    })
    .addCase(parkingProductsThunks.getParkingProducts.pending, (state) => {
        state.parkingProducts = {
            data: [],
            loading: true,
        };
    })
    .addCase(parkingProductsThunks.getParkingProducts.fulfilled, (state, action) => {
        state.hasUnlimitedEntryRights = action.payload.some(p => p.hasUnlimitedEntryRights);
        state.parkingProducts = {
            ...state.parkingProducts,
            data: action.payload,
            loading: false,
        };
    })
    .addCase(parkingProductsThunks.getParkingProducts.rejected, (state, action) => {
        state.parkingProducts = {
            ...state.parkingProducts,
            loading: false,
            error: action.payload,
        };
    })
    .addCase(parkingProductsThunks.getLocatedParkingProductsThunk.pending, (state, _) => {
        state.locatedParkingProducts.loading = true;
    })
    .addCase(parkingProductsThunks.getLocatedParkingProductsThunk.fulfilled, (state, action) => {
        state.locatedParkingProducts.loading = false;
        state.locatedParkingProducts.data = action.payload;
        state.hasUnlimitedEntryRights = Array.isArray(action.payload) && action.payload.some(lpp => lpp.parkingProducts.some(p => p.hasUnlimitedEntryRights));
      })
    .addCase(parkingProductsThunks.getLocatedParkingProductsThunk.rejected, (state, action) => {
        state.locatedParkingProducts.loading = false;
        state.locatedParkingProducts.data = [];
        state.locatedParkingProducts.error = action.payload;
    })
    .addCase(parkingProductsThunks.delegateParkingRightsThunk.pending, (state, _) => {
        state.loading = true;
    })
    .addCase(parkingProductsThunks.delegateParkingRightsThunk.fulfilled, (state, action) => {
        if (!action.payload.ok) {
            state.loading = false;
            state.error = action.payload.error;
            return;
        }
        state.loading = false;
        applyOptimisticInviteParkingRightMyProducts(state, action.payload.value, action.meta.arg);
        applyOptimisticInviteOnParkingMyFleet(state, action.payload.value, action.meta.arg);
        
    })
    .addCase(parkingProductsThunks.delegateParkingRightsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || null;
    })
    .addCase(parkingProductsThunks.getVehiclesThunk.pending, (state, action) => {
        state.vehiclesMyProducts.pages[action.meta.arg.page] = {
            ...state.vehiclesMyProducts.pages[action.meta.arg.page],
            loading: true,
            query: action.meta.arg,
            data: [],
            requestId: action.meta.requestId,
        };
    })
    .addCase(parkingProductsThunks.getVehiclesThunk.fulfilled, (state, action) => {
        if (!action.meta.arg.countersOnly) {
          state.vehiclesMyProducts.pages[action.meta.arg.page] = {
            ...state.vehiclesMyProducts.pages[action.meta.arg.page],
            loading: false,
            query: action.meta.arg,
            data: action.payload.parkingRights,
            requestId: action.meta.requestId,
          };
        }
        state.vehiclesMyProducts.totalPages = action.payload?.totalPages as number;
        state.vehiclesMyProducts.totalRecords = action.payload?.totalRecords as number;
    })
    .addCase(parkingProductsThunks.getVehiclesThunk.rejected, (state, action) => {
        const page = state.vehiclesMyProducts.pages[action.meta.arg.page];
        if (action.meta.aborted && page?.requestId && action.meta.requestId !== page.requestId) {
            return;
        }

        state.vehiclesMyProducts.pages[action.meta.arg.page] = {
            ...page,
            loading: false,
            data: [],
            error: action.payload,
            requestId: action.meta.requestId,
        };
    })
    .addCase(parkingProductsThunks.updateDelegationThunk.pending, (state, action) => { 
        state.selectedVehicleAssignment.loading = true;
        state.loading = true;
    })
    .addCase(parkingProductsThunks.updateDelegationThunk.fulfilled, (state, action) => {
        state.loading = false;
        if (!action.payload.ok) {
            state.error = action.payload.error;
            return;
        }
        const { original, response } = action.payload.value;

        if (original) {
            applyOptimisticUpdateParkingRightMyProducts(state, response, action.meta.arg, original);
            applyOptimisticUpdateParkingRightMyFleet(state, response, action.meta.arg, original);
        }
    })
    .addCase(parkingProductsThunks.updateDelegationThunk.rejected, (state, action) => { 
        state.selectedVehicleAssignment.loading = false;
        state.loading = false;
        state.error = action.payload || null;
    })
    .addCase(parkingProductsThunks.getDelegeesThunk.pending, (state, action) => {
        const pageNumber = action.meta.arg.request.pagination.pageNumber;
        if (!state.delegees.pages) {
            state.delegees.pages = {};
        }
        state.delegees.pages[pageNumber] = {
            loading: true,
            data: [],
        };
    })
    .addCase(parkingProductsThunks.getDelegeesThunk.fulfilled, (state, action) => {
        const pageNumber = action.meta.arg.request.pagination.pageNumber;
        state.delegees.totalPages = action.payload.pages;
        state.delegees.totalRecords = action.payload.totalRecords;
        if (!state.delegees.pages) {
            state.delegees.pages = {};
        }
        state.delegees.pages[pageNumber] = {
            loading: false,
            data: action.payload.delegees,
        };
    })
    .addCase(parkingProductsThunks.getDelegeesThunk.rejected, (state, action) => {
        const pageNumber = action.meta.arg.request.pagination.pageNumber;
        if (!state.delegees.pages) {
            state.delegees.pages = {};
        }
        state.delegees.pages[pageNumber] = {
            data: [],
            loading: false,
            error: action.payload,
        };
    })
    .addCase(parkingProductsThunks.getDelegeeDetailsThunk.pending, (state, action) => {
        state.delegeeDetails.loading = true;
    })
    .addCase(parkingProductsThunks.getDelegeeDetailsThunk.fulfilled, (state, action) => {
        state.delegeeDetails.loading = false;
        state.delegeeDetails.data = action.payload;
    })
    .addCase(parkingProductsThunks.getDelegeeDetailsThunk.rejected, (state, action) => {
        state.delegeeDetails.loading = false;
        state.delegeeDetails.error = action.payload || null;
    })
    .addCase(parkingProductsThunks.getGetVehicleAssignmentsMyFleetThunk.pending, (state, action) => {
        const pageNumber = action.meta.arg.query.page;
        if (!state.vehicleAssignmentsMyFleetOverview.pages) {
            state.vehicleAssignmentsMyFleetOverview.pages = {};
        }
        state.vehicleAssignmentsMyFleetOverview.pages[pageNumber] = {
            loading: true,
            data: [],
            query: action.meta.arg.query,
        };
    })
    .addCase(parkingProductsThunks.getGetVehicleAssignmentsMyFleetThunk.fulfilled, (state, action) => {
        const pageNumber = action.meta.arg.query.page;
        state.vehicleAssignmentsMyFleetOverview.totalPages = action.payload.pages as number;
        state.vehicleAssignmentsMyFleetOverview.totalRecords =  action.payload.totalRecords as number;
        state.vehicleAssignmentsMyFleetOverview.totalUniqueVehicles = action.payload.totalUniqueVehicles as number; 
        if (!state.vehicleAssignmentsMyFleetOverview.pages) {
            state.vehicleAssignmentsMyFleetOverview.pages = {};
        }
        state.vehicleAssignmentsMyFleetOverview.pages[pageNumber] = {
            loading: false,
            data: action.payload.vehicleAssignments,
            query: action.meta.arg.query,
        };
    })
    .addCase(parkingProductsThunks.getGetVehicleAssignmentsMyFleetThunk.rejected, (state, action) => {
        const pageNumber = action.meta.arg.query.page;
        if (!state.vehicleAssignmentsMyFleetOverview.pages) {
            state.vehicleAssignmentsMyFleetOverview.pages = {};
        }
        state.vehicleAssignmentsMyFleetOverview.pages[pageNumber] = {
            loading: false,
            data: [],
            error: action.payload,
            query: action.meta.arg.query,
        };
    })
    .addDefaultCase((state, action) => oldReducer(state, action));
});

function calculateDelta(pmcId: number, oldPmcIds: number[], newPmcIds: number[]): number {
  const inOld = oldPmcIds.includes(pmcId);
  const inNew = newPmcIds.includes(pmcId);

  if (!inOld && inNew) {
      return 1;
  } else if (inOld && !inNew) {
    return -1;
  } else {
    return 0;
  }
}